import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../App';
import { toast } from 'sonner';
import {
  Upload, FileSpreadsheet, Download, Loader2, CheckCircle2, AlertTriangle,
  X, ChevronRight, History, FileText, Sparkles, Building2, BookOpen, Trash2,
} from 'lucide-react';

const StatCard = ({ icon: Icon, label, value, color = 'navy' }) => {
  const tones = {
    navy: 'bg-[#1A2A4F]/5 text-[#1A2A4F] border-[#1A2A4F]/15',
    orange: 'bg-[#F5A623]/10 text-[#F5A623] border-[#F5A623]/25',
    green: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    red: 'bg-rose-50 text-rose-700 border-rose-200',
  };
  return (
    <div className={`rounded-xl border ${tones[color]} p-4 flex items-center gap-3`}>
      <Icon className="w-5 h-5 shrink-0" />
      <div>
        <p className="text-2xl font-bold leading-none">{value}</p>
        <p className="text-[11px] font-semibold uppercase tracking-wider opacity-80 mt-1">{label}</p>
      </div>
    </div>
  );
};

const ProgressBar = ({ pct, label }) => (
  <div className="space-y-2">
    <div className="flex items-center justify-between text-sm">
      <span className="font-semibold text-[#1A2A4F]">{label}</span>
      <span className="font-bold tabular-nums">{Math.round(pct)}%</span>
    </div>
    <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
      <div
        className="h-full bg-gradient-to-r from-[#F5A623] to-[#1A2A4F] transition-[width] duration-500"
        style={{ width: `${pct}%` }}
      />
    </div>
  </div>
);

// Required columns must match the backend (see import_courses.py REQUIRED).
const REQUIRED_FIELDS = new Set(['University Name', 'Program Name', 'Course Title', 'Currency']);

const PreviewGrid = ({ preview }) => {
  const allRows = preview.preview || [];
  // Discover ALL columns dynamically — every key on the row except meta keys.
  const columns = React.useMemo(() => {
    const seen = new Set();
    for (const r of allRows) {
      for (const k of Object.keys(r)) {
        if (k.startsWith('_')) continue;
        seen.add(k);
      }
    }
    return [...seen];
  }, [allRows]);

  // Build a (row -> field -> error) map so we can decorate individual cells.
  const errorMap = React.useMemo(() => {
    const m = {};
    for (const e of (preview.row_errors || [])) {
      const row = e.row;
      if (!m[row]) m[row] = {};
      // a single field may collect multiple issues — keep them as an array
      if (!m[row][e.field]) m[row][e.field] = [];
      m[row][e.field].push(e);
    }
    return m;
  }, [preview.row_errors]);

  const SHOW_LIMIT = 200;
  const shown = allRows.slice(0, SHOW_LIMIT);

  return (
    <div className="border border-gray-100 rounded-lg overflow-hidden" data-testid="preview-data-table">
      {/* Legend */}
      <div className="flex flex-wrap items-center gap-4 px-3 py-2 bg-gray-50 border-b border-gray-100 text-[11px] text-gray-600">
        <span className="inline-flex items-center gap-1.5"><span className="inline-block w-2.5 h-2.5 rounded-sm bg-rose-100 border border-rose-300" /> Required cell missing</span>
        <span className="inline-flex items-center gap-1.5"><span className="inline-block w-2.5 h-2.5 rounded-sm bg-amber-100 border border-amber-300" /> Invalid format</span>
        <span className="inline-flex items-center gap-1.5"><span className="inline-block w-2.5 h-2.5 rounded-sm bg-gray-200 border border-gray-300" /> Blank row</span>
        <span className="inline-flex items-center gap-1.5"><span className="inline-block w-2.5 h-2.5 rounded-sm bg-emerald-100 border border-emerald-300" /> Valid row</span>
      </div>
      <div className="overflow-auto max-h-[480px]">
        <table className="min-w-max text-xs">
          <thead className="bg-gray-50 text-left sticky top-0 z-10 shadow-[0_1px_0_0_rgba(0,0,0,0.05)]">
            <tr>
              <th className="px-3 py-2 font-bold text-[#1A2A4F] sticky left-0 bg-gray-50 z-20 border-r border-gray-100">Row</th>
              {columns.map((col) => (
                <th
                  key={col}
                  className={`px-3 py-2 font-bold text-[#1A2A4F] whitespace-nowrap ${REQUIRED_FIELDS.has(col) ? 'after:content-["*"] after:text-rose-500 after:ml-0.5' : ''}`}
                  title={REQUIRED_FIELDS.has(col) ? 'Required column' : 'Optional column'}
                >
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {shown.map((r) => {
              const rowErrs = errorMap[r._row] || {};
              const isBlankRow = columns.every((c) => !String(r[c] ?? '').trim());
              const rowBg = isBlankRow ? 'bg-gray-100' : r._valid ? 'bg-emerald-50/40' : 'bg-rose-50/40';
              return (
                <tr key={r._row} className={`${rowBg} border-b border-gray-50`} data-testid={`preview-row-${r._row}`}>
                  <td className="px-3 py-2 font-mono text-[#1A2A4F]/70 sticky left-0 bg-inherit border-r border-gray-100">
                    {r._row}
                    {isBlankRow && (
                      <span className="ml-1.5 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider bg-gray-300 text-gray-700">
                        empty
                      </span>
                    )}
                  </td>
                  {columns.map((col) => {
                    const val = String(r[col] ?? '');
                    const cellErrs = rowErrs[col];
                    const isBlank = !val.trim();
                    const isMissingRequired = isBlank && REQUIRED_FIELDS.has(col);
                    const hasInvalid = cellErrs?.some((e) => e.type === 'invalid_format' || e.type === 'warning');
                    let cellCls = 'px-3 py-2 whitespace-nowrap max-w-[260px] overflow-hidden text-ellipsis';
                    if (isMissingRequired) {
                      cellCls += ' bg-rose-100 text-rose-900 ring-1 ring-inset ring-rose-400 font-semibold';
                    } else if (hasInvalid) {
                      cellCls += ' bg-amber-100 text-amber-900 ring-1 ring-inset ring-amber-400';
                    } else if (isBlank) {
                      cellCls += ' bg-gray-50 text-gray-400 italic';
                    }
                    const tip = cellErrs && cellErrs.length
                      ? cellErrs.map((e) => `${e.message}${e.fix ? ' — ' + e.fix : ''}`).join(' | ')
                      : isMissingRequired ? `Required field "${col}" is empty` : undefined;
                    return (
                      <td key={col} className={cellCls} title={tip}>
                        {isBlank ? (isMissingRequired ? 'missing' : '—') : val}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="px-3 py-2 text-[11px] text-muted-foreground bg-gray-50 border-t border-gray-100 flex justify-between items-center">
        <span>
          Showing {shown.length} of {preview.total_rows} rows · {Object.keys(errorMap).length} row(s) with issues
        </span>
        {allRows.length > SHOW_LIMIT && (
          <span className="text-[#F5A623] font-semibold">
            Only the first {SHOW_LIMIT} are previewed — the full file will be processed on import.
          </span>
        )}
      </div>
    </div>
  );
};

const ImportCoursesPage = () => {
  const { api } = useAuth();
  const fileRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);     // backend preview response
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [result, setResult] = useState(null);       // backend commit response
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [clearingHistory, setClearingHistory] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const r = await api().get('/import/courses/history');
      setHistory(Array.isArray(r.data) ? r.data : []);
    } catch { /* silent */ }
    setHistoryLoading(false);
  }, [api]);
  useEffect(() => { loadHistory(); }, [loadHistory]);

  const handleFile = async (f) => {
    if (!f) return;
    if (!/\.(xlsx|xlsm|csv)$/i.test(f.name)) {
      toast.error('Unsupported file type. Use .xlsx, .xlsm or .csv');
      return;
    }
    if (f.size > 15 * 1024 * 1024) {
      toast.error('File too large (max 15 MB). Split into smaller batches.');
      return;
    }
    setFile(f);
    setPreview(null);
    setResult(null);
    setUploading(true);
    const fd = new FormData();
    fd.append('file', f);
    try {
      const r = await api().post('/import/courses/preview', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setPreview(r.data);
      toast.success(`Parsed ${r.data.total_rows} rows · ${r.data.valid_rows} valid`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not read the file');
      setFile(null);
    }
    setUploading(false);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    if (e.dataTransfer.files?.[0]) handleFile(e.dataTransfer.files[0]);
  };

  const downloadTemplate = async () => {
    try {
      const r = await api().get('/import/courses/template', { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'edubridge-import-template.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Could not download template');
    }
  };

  const downloadErrorReport = async (importId) => {
    try {
      const r = await api().get(`/import/courses/${importId}/error-report.csv`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `import-${importId}-errors.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Could not download error report');
    }
  };

  const startImport = async () => {
    if (!preview?.upload_id) return;
    if (preview.header_errors?.length) {
      toast.error('Fix header errors first');
      return;
    }
    setImporting(true);
    setImportProgress(5);
    // Smooth fake progress — backend is fast but we keep a visual heartbeat
    const tick = setInterval(() => {
      setImportProgress((p) => (p < 88 ? p + Math.random() * 6 : p));
    }, 220);
    try {
      const r = await api().post('/import/courses/commit', { upload_id: preview.upload_id });
      setImportProgress(100);
      setResult(r.data);
      toast.success(`Imported ${r.data.summary?.imported || 0} programs`);
      await loadHistory();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Import failed');
    } finally {
      clearInterval(tick);
      setTimeout(() => setImporting(false), 500);
    }
  };

  const reset = () => {
    setFile(null); setPreview(null); setResult(null); setImportProgress(0);
    if (fileRef.current) fileRef.current.value = '';
  };

  return (
    <div className="space-y-8" data-testid="import-courses-page">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#1A2A4F] via-[#243a6a] to-[#1A2A4F] text-white p-8">
        <div className="absolute -top-16 -right-16 w-72 h-72 rounded-full bg-[#F5A623]/30 blur-3xl pointer-events-none" />
        <div className="relative flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <p className="text-[#F5A623] font-semibold tracking-widest text-xs uppercase">
              Bulk Import
            </p>
            <h1 className="mt-2 text-3xl sm:text-4xl font-bold">
              One-click Course & University Import
            </h1>
            <p className="mt-2 text-sm opacity-80 max-w-xl">
              Upload one Excel/CSV and we&apos;ll auto-create Universities, Campuses, Programs,
              Currencies, Subjects, Sessions — no duplicates, no manual data entry.
            </p>
          </div>
          <button
            onClick={downloadTemplate}
            className="inline-flex items-center gap-2 bg-[#F5A623] text-white font-bold px-5 py-2.5 rounded-md hover:brightness-95 transition"
            data-testid="download-template-btn"
          >
            <Download className="w-4 h-4" /> Download sample template
          </button>
        </div>
      </div>

      {/* Upload zone */}
      {!preview && (
        <div
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          className={`relative rounded-2xl border-2 border-dashed p-10 text-center transition
            ${dragging ? 'border-[#F5A623] bg-[#F5A623]/5' : 'border-gray-200 bg-white'}`}
          data-testid="upload-dropzone"
        >
          <input
            ref={fileRef}
            type="file"
            accept=".xlsx,.xlsm,.csv"
            onChange={(e) => handleFile(e.target.files?.[0])}
            className="hidden"
            data-testid="import-file-input"
          />
          <div className="mx-auto w-14 h-14 rounded-2xl bg-[#1A2A4F]/5 text-[#1A2A4F] flex items-center justify-center">
            {uploading ? <Loader2 className="w-7 h-7 animate-spin" /> : <Upload className="w-7 h-7" />}
          </div>
          <h2 className="mt-4 text-lg font-bold text-[#1A2A4F]">
            {uploading ? 'Reading your file…' : 'Drag & drop your Excel or CSV file here'}
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            or
            <button
              onClick={() => fileRef.current?.click()}
              className="ml-1 text-[#F5A623] font-semibold hover:underline"
              data-testid="browse-file-btn"
            >
              browse from your computer
            </button>
          </p>
          <p className="mt-3 text-xs text-muted-foreground">
            Supported: .xlsx, .xlsm, .csv · max 15 MB · expected sheet: &quot;University Programs Data&quot;
          </p>
          <div className="mt-6 inline-flex items-center gap-2 text-xs text-[#1A2A4F]/70">
            <Sparkles className="w-3.5 h-3.5 text-[#F5A623]" />
            University → Campus → Degree → Program — auto-matched, never duplicated.
          </div>
        </div>
      )}

      {/* Preview */}
      {preview && !result && (
        <div className="bg-white rounded-2xl border border-gray-100 p-6 space-y-5" data-testid="preview-panel">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-md bg-[#F5A623]/10 text-[#F5A623] flex items-center justify-center">
                <FileSpreadsheet className="w-5 h-5" />
              </div>
              <div>
                <p className="font-bold text-[#1A2A4F]">{file?.name || 'Uploaded file'}</p>
                <p className="text-xs text-muted-foreground">
                  {preview.total_rows} rows · {preview.valid_rows} valid · {preview.invalid_rows} invalid
                </p>
              </div>
            </div>
            <button onClick={reset} className="text-gray-400 hover:text-gray-600" data-testid="cancel-upload-btn">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <StatCard icon={FileText} label="Total rows" value={preview.total_rows} />
            <StatCard icon={CheckCircle2} label="Valid rows" value={preview.valid_rows} color="green" />
            <StatCard icon={AlertTriangle} label="Invalid rows" value={preview.invalid_rows} color={preview.invalid_rows ? 'red' : 'orange'} />
            <StatCard icon={Sparkles} label="Ready to import" value={preview.valid_rows} color="orange" />
          </div>

          {preview.header_errors?.length > 0 && (
            <div className="rounded-md bg-rose-50 border border-rose-200 p-4 text-sm text-rose-900" data-testid="header-errors">
              <p className="font-bold mb-1">Header errors — fix before importing</p>
              <ul className="list-disc ml-5">
                {preview.header_errors.map((e, i) => (
                  <li key={i}><b>{e.field}</b>: {e.message} <span className="opacity-70">— {e.fix}</span></li>
                ))}
              </ul>
            </div>
          )}

          {preview.row_errors?.length > 0 && (
            <details className="rounded-md bg-amber-50 border border-amber-200 p-4 text-sm">
              <summary className="font-semibold text-amber-900 cursor-pointer">
                {preview.row_errors.length} row issue(s) — click to expand
              </summary>
              <div className="mt-3 max-h-60 overflow-auto">
                <table className="w-full text-xs" data-testid="preview-errors-table">
                  <thead className="text-left">
                    <tr className="border-b border-amber-200">
                      <th className="py-1 pr-3">Row</th>
                      <th className="py-1 pr-3">Field</th>
                      <th className="py-1 pr-3">Issue</th>
                      <th className="py-1">Fix</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.row_errors.slice(0, 50).map((e, i) => (
                      <tr key={i} className="border-b border-amber-100">
                        <td className="py-1 pr-3 font-mono">{e.row}</td>
                        <td className="py-1 pr-3">{e.field}</td>
                        <td className="py-1 pr-3">{e.message}</td>
                        <td className="py-1 opacity-70">{e.fix}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>
          )}

          {/* Data preview table — ALL parsed columns + per-cell error highlights */}
          <PreviewGrid preview={preview} />

          {/* Action bar */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-2">
            <p className="text-xs text-muted-foreground">
              Existing records will be matched by name — no duplicates will be created.
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={reset}
                className="px-4 py-2.5 text-sm font-semibold border border-gray-200 rounded-md hover:bg-gray-50"
                data-testid="discard-preview-btn"
              >
                Discard
              </button>
              <button
                onClick={startImport}
                disabled={importing || preview.valid_rows === 0 || (preview.header_errors?.length > 0)}
                className="inline-flex items-center gap-2 bg-[#F5A623] text-white font-bold px-5 py-2.5 rounded-md hover:brightness-95 disabled:opacity-50"
                data-testid="start-import-btn"
              >
                {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <ChevronRight className="w-4 h-4" />}
                Start import · {preview.valid_rows} rows
              </button>
            </div>
          </div>

          {importing && (
            <div className="pt-2" data-testid="import-progress">
              <ProgressBar pct={importProgress} label="Importing…" />
            </div>
          )}
        </div>
      )}

      {/* Result */}
      {result && (
        <div className="bg-white rounded-2xl border border-emerald-200 p-6" data-testid="import-result">
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center shrink-0">
              <CheckCircle2 className="w-6 h-6" />
            </div>
            <div className="flex-1">
              <h2 className="text-xl font-bold text-[#1A2A4F]">Import complete</h2>
              <p className="text-sm text-muted-foreground mt-1">
                Your data is now live on the public site and in Core Settings.
              </p>
            </div>
            <button
              onClick={reset}
              className="text-sm font-semibold text-[#F5A623] hover:underline"
              data-testid="import-another-btn"
            >
              Import another file
            </button>
          </div>
          <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-3">
            <StatCard icon={CheckCircle2} label="Programs imported" value={result.summary?.imported || 0} color="green" />
            <StatCard icon={Building2} label="Universities added" value={result.summary?.universities_created || 0} />
            <StatCard icon={BookOpen} label="Campuses added" value={result.summary?.campuses_created || 0} />
            <StatCard icon={Sparkles} label="Master data added" value={result.summary?.master_data_created || 0} color="orange" />
          </div>
          <div className="mt-4 flex items-center gap-2">
            {result.summary?.skipped > 0 && (
              <button
                onClick={() => downloadErrorReport(result.import_id)}
                className="inline-flex items-center gap-2 text-sm font-semibold border border-rose-200 text-rose-700 px-4 py-2 rounded-md hover:bg-rose-50"
                data-testid="download-error-report-btn"
              >
                <Download className="w-4 h-4" /> Download error report ({result.summary.skipped} skipped)
              </button>
            )}
            <a
              href="/courses"
              className="inline-flex items-center gap-2 text-sm font-semibold text-[#1A2A4F] hover:underline"
              data-testid="view-courses-link"
            >
              View on public Courses page <ChevronRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      )}

      {/* Import history */}
      <div className="bg-white rounded-2xl border border-gray-100 p-6" data-testid="import-history-panel">
        <div className="flex items-center justify-between gap-2 mb-4">
          <div className="flex items-center gap-2">
            <History className="w-4 h-4 text-muted-foreground" />
            <h2 className="text-base font-bold text-[#1A2A4F]">Import history</h2>
          </div>
          {history.length > 0 && (
            <button
              onClick={async () => {
                const ok = window.confirm(`Delete ALL ${history.length} import log(s)? This only removes audit history — imported data stays in the system.`);
                if (!ok) return;
                setClearingHistory(true);
                try {
                  const r = await api().delete('/import/courses/history');
                  toast.success(`Cleared ${r.data?.deleted_imports || 0} import log(s).`);
                  await loadHistory();
                } catch (e) {
                  toast.error(e?.response?.data?.detail || 'Could not clear logs');
                }
                setClearingHistory(false);
              }}
              disabled={clearingHistory}
              className="inline-flex items-center gap-1.5 text-xs font-semibold border border-rose-200 text-rose-700 px-3 py-1.5 rounded-md hover:bg-rose-50 disabled:opacity-50"
              data-testid="clear-history-btn"
              title="Delete every previous import log (does NOT delete imported courses/universities)"
            >
              {clearingHistory ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
              Clear all logs
            </button>
          )}
        </div>
        {historyLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : history.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="history-empty">
            No imports yet. Upload your first file above.
          </p>
        ) : (
          <div className="overflow-auto">
            <table className="min-w-full text-sm" data-testid="history-table">
              <thead className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                <tr className="border-b border-gray-100">
                  <th className="py-2 pr-4">File</th>
                  <th className="py-2 pr-4">Uploaded by</th>
                  <th className="py-2 pr-4">When</th>
                  <th className="py-2 pr-4 text-right">Imported</th>
                  <th className="py-2 pr-4 text-right">Skipped</th>
                  <th className="py-2 pr-4">Status</th>
                  <th className="py-2"></th>
                </tr>
              </thead>
              <tbody>
                {history.map((h) => (
                  <tr key={h.id} className="border-b border-gray-50" data-testid={`history-row-${h.id}`}>
                    <td className="py-2 pr-4 font-semibold text-[#1A2A4F]">{h.filename || '—'}</td>
                    <td className="py-2 pr-4">{h.uploaded_by || '—'}</td>
                    <td className="py-2 pr-4">
                      {h.started_at ? new Date(h.started_at).toLocaleString() : '—'}
                    </td>
                    <td className="py-2 pr-4 text-right font-bold text-emerald-700 tabular-nums">{h.imported_rows}</td>
                    <td className={`py-2 pr-4 text-right tabular-nums font-bold ${h.skipped_rows ? 'text-rose-700' : 'text-gray-400'}`}>
                      {h.skipped_rows}
                    </td>
                    <td className="py-2 pr-4">
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-emerald-50 text-emerald-700">
                        {h.status || 'completed'}
                      </span>
                    </td>
                    <td className="py-2">
                      <div className="flex items-center gap-2 justify-end">
                        <button
                          onClick={() => downloadErrorReport(h.id)}
                          className="text-xs text-[#F5A623] font-semibold hover:underline"
                          data-testid={`history-errors-${h.id}`}
                        >
                          Error report
                        </button>
                        <button
                          onClick={async () => {
                            const ok = window.confirm(`Delete log for "${h.filename || h.id}"? Imported data is not affected.`);
                            if (!ok) return;
                            setDeletingId(h.id);
                            try {
                              await api().delete(`/import/courses/history/${h.id}`);
                              toast.success('Log entry deleted');
                              await loadHistory();
                            } catch (e) {
                              toast.error(e?.response?.data?.detail || 'Delete failed');
                            }
                            setDeletingId(null);
                          }}
                          disabled={deletingId === h.id}
                          className="text-xs text-rose-600 hover:text-rose-800 inline-flex items-center gap-1 disabled:opacity-50"
                          data-testid={`history-delete-${h.id}`}
                          title="Delete this log entry"
                        >
                          {deletingId === h.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ImportCoursesPage;
